import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./TicketChat.css";
import MessageItem from "./MessageItem";
import { getRefId } from "./ticketConstants";
import {
  fetchTicketByIdAPI,
  fetchTicketPageAPI,
  TicketMessageAPI,
} from "../../../utils/api/teamTicket";
import { Context } from "../../../context/Context";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import { getTicketText } from "./ticketText";
import {
  Button,
  Input,
  NoticeBox,
  Bookmark,
  Check,
  Circle,
  CircleX,
  Clock3,
  Headphones,
  Plus,
  Shield,
  Store,
  Timer,
  Wrench,
} from "../../ui";

const STATUS_ACTIONS = [
  { status: "comment", label: "Comment", icon: <Bookmark /> },
  { status: "new", label: "New", icon: <Plus /> },
  { status: "inProgress", label: "In Progress", icon: <Clock3 /> },
  { status: "completed", label: "Completed", icon: <Check /> },
  { status: "rejected", label: "Rejected", icon: <CircleX /> },
  { status: "incompleteData", label: "Incomplete Data", icon: <Timer /> },
  { status: "pending", label: "Pending", icon: <Circle /> },
  { status: "support", label: "Support", icon: <Headphones /> },
];

const STAGES_ACTIONS = [
  { status: "client", label: "Client", icon: <Store /> },
  { status: "app", label: "App", icon: <Wrench /> },
  { status: "team", label: "Team", icon: <Check /> },
  { status: "manager", label: "manager", icon: <Shield /> },
  { status: "integration", label: "integration", icon: <Shield /> },
];

export const CHAT_PAGE_SIZE = 50;

const timeOf = (message) => new Date(message.createdAt).getTime();
// Same order as the server's cursor: createdAt, then _id (ObjectId hex strings compare like the ids).
const byAge = (a, b) => timeOf(a) - timeOf(b) || String(a._id).localeCompare(String(b._id));
const cursorOf = (message) => `${timeOf(message)}_${message._id}`;

// Adds pages together without repeating a message (a newer copy of the same _id wins) and keeps chronological order.
export const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map((message) => [message._id, message]));
  incoming.forEach((message) => byId.set(message._id, message));
  return [...byId.values()].sort(byAge);
};

// The newest page just fetched (first load, or the refresh after sending) against what is already shown.
// - nothing shown yet, or the page starts after the newest message shown (more than a page of new messages
//   arrived): start over from the page so the conversation has no hole;
// - otherwise merge, and keep "is there more" of whichever side reaches further back.
export const applyLatestPage = (view, page, pageHasMore) => {
  if (!view.messages.length) return { messages: page, hasMore: pageHasMore, reset: true };
  if (!page.length) return { ...view, reset: false };
  const newestShown = view.messages[view.messages.length - 1];
  const oldestShown = view.messages[0];
  if (byAge(page[0], newestShown) > 0) return { messages: page, hasMore: pageHasMore, reset: true };
  const pageIsOlder = byAge(page[0], oldestShown) < 0;
  return { messages: mergeMessages(view.messages, page), hasMore: pageIsOlder ? pageHasMore : view.hasMore, reset: false };
};

// The parent keys this component by ticketId, so switching tickets starts
// from a clean state instead of briefly showing the previous ticket.
export default function TicketChat({ ticketId, refresh }) {
  const { handleTokenExpiration, currentUserInfo, lang } = useContext(Context);
  // TICKET_CHAT_PAGING off: the whole conversation in one request, exactly as before.
  const paged = isFeatureEnabled(FEATURE_NAMES.TICKET_CHAT_PAGING);
  const text = getTicketText(lang);

  const [ticket, setTicket] = useState(null);
  const [view, setView] = useState({ messages: [], hasMore: false });
  const messages = view.messages;
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderFailed, setOlderFailed] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [sendingAction, setSendingAction] = useState(null);

  const messagesRef = useRef(null);
  const mountedRef = useRef(true);
  const sendingRef = useRef(false);
  // Paging: the newest answer wins; a page of older messages is dropped if the list was rebuilt meanwhile;
  // `anchorRef` remembers the scroll geometry before older messages are prepended.
  const latestRequestRef = useRef(0);
  const epochRef = useRef(0);
  const anchorRef = useRef(null);
  const lastIdRef = useRef(null);
  const olderBusyRef = useRef(false);
  // The context recreates this function on every provider render; reading it
  // through a ref keeps fetchTicket stable so the load effect runs once per ticket.
  const tokenExpiredRef = useRef(handleTokenExpiration);
  tokenExpiredRef.current = handleTokenExpiration;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (paged) return;
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, paged]);

  // Paged: prepending older messages keeps the reader where they were; a new newest message (first load, own send,
  // a reply) scrolls to the bottom; a refresh that changes nothing does not move the view.
  useLayoutEffect(() => {
    if (!paged) return;
    const element = messagesRef.current;
    if (!element) return;
    if (anchorRef.current) {
      const { height, top } = anchorRef.current;
      anchorRef.current = null;
      element.scrollTop = element.scrollHeight - height + top;
      return;
    }
    const newestId = messages.length ? messages[messages.length - 1]._id : null;
    if (newestId !== lastIdRef.current) {
      lastIdRef.current = newestId;
      element.scrollTop = element.scrollHeight;
    }
  }, [messages, paged]);

  const fetchTicket = useCallback(async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) return tokenExpiredRef.current();

    if (paged) {
      // No fallback to the unbounded request: a failure shows the retry state.
      const requestId = ++latestRequestRef.current;
      const result = await fetchTicketPageAPI(token, ticketId, { limit: CHAT_PAGE_SIZE });
      if (!mountedRef.current || requestId !== latestRequestRef.current) return;
      if (result.ok && result.data?.ticket) {
        const page = result.data.messages || [];
        setTicket(result.data.ticket);
        setView((current) => {
          const next = applyLatestPage(current, page, Boolean(result.data.hasMore));
          if (next.reset) epochRef.current += 1;
          return { messages: next.messages, hasMore: next.hasMore };
        });
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
      return;
    }

    // The API helper resolves to undefined on any failure.
    const data = await fetchTicketByIdAPI(token, ticketId);
    if (!mountedRef.current) return;

    if (data?.ticket) {
      setTicket(data.ticket);
      setView({ messages: data.messages || [], hasMore: false });
      setLoadFailed(false);
    } else {
      setLoadFailed(true);
    }
  }, [ticketId, paged]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const loadOlder = async () => {
    if (olderBusyRef.current || !view.hasMore || !messages.length) return;
    const token = localStorage.getItem("auth-token");
    if (!token) return tokenExpiredRef.current();

    olderBusyRef.current = true;
    const epoch = epochRef.current;
    setLoadingOlder(true);
    setOlderFailed(false);
    try {
      const result = await fetchTicketPageAPI(token, ticketId, {
        limit: CHAT_PAGE_SIZE,
        before: cursorOf(messages[0]),
      });
      // Unmounted, or the list was rebuilt while this was in flight: the page no longer fits.
      if (!mountedRef.current || epoch !== epochRef.current) return;
      if (result.ok && Array.isArray(result.data?.messages)) {
        const element = messagesRef.current;
        if (element) anchorRef.current = { height: element.scrollHeight, top: element.scrollTop };
        setView((current) => ({
          messages: mergeMessages(current.messages, result.data.messages),
          hasMore: Boolean(result.data.hasMore),
        }));
      } else {
        setOlderFailed(true);
      }
    } finally {
      olderBusyRef.current = false;
      if (mountedRef.current) setLoadingOlder(false);
    }
  };

  const sendMessage = async (status = "comment", isStage = false) => {
    if (sendingRef.current) return;

    const currentValue = isStage ? ticket.processingStage : ticket.status;
    if ((!newMessage.trim() && status === "comment") || status === currentValue)
      return;

    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();

    sendingRef.current = true;
    setSendingAction(status);
    try {
      // The message endpoint doesn't return `success`, so the helper can't
      // report the outcome; re-fetching shows what actually landed.
      await TicketMessageAPI(token, ticketId, {
        message: newMessage,
        status,
        isStage,
      });
      if (mountedRef.current) setNewMessage("");
      await Promise.all([fetchTicket(), refresh()]);
    } finally {
      sendingRef.current = false;
      if (mountedRef.current) setSendingAction(null);
    }
  };

  if (!ticket) {
    return (
      <div className="ticket-chat-container ticket-chat-placeholder">
        {loadFailed ? (
          <NoticeBox variant="danger" title="Could not load this ticket">
            <Button size="sm" appearance="soft" onClick={fetchTicket}>
              Retry
            </Button>
          </NoticeBox>
        ) : (
          <p role="status" className="ticket-chat-status">
            Loading ticket…
          </p>
        )}
      </div>
    );
  }

  const isSending = sendingAction !== null;
  const renderAction = ({ status, label, icon }, isStage) => {
    const isActive =
      (isStage ? ticket.processingStage : ticket.status) === status;
    return (
      <Button
        key={status}
        variant="primary"
        appearance="filled"
        size="sm"
        className={`action-btn ${status} ${isActive ? "active" : ""}`}
        onClick={() => sendMessage(status, isStage)}
        title={label}
        icon={icon}
        aria-pressed={status === "comment" ? undefined : isActive}
        loading={sendingAction === status}
        disabled={
          isSending || (status === "comment" && !newMessage.trim())
        }
      >
        {label}
      </Button>
    );
  };

  return (
    <div className="ticket-chat-container">
      <p className="ticket-chat-title-3">{ticket.storeId?.businessName}</p>

      <h2 className="ticket-chat-title">{ticket.title}</h2>

      <div
        className="ticket-chat-messages"
        ref={messagesRef}
        role="log"
        aria-label="Ticket messages"
        tabIndex={0}
      >
        {paged && view.hasMore && (
          <div className="ticket-chat-older">
            {olderFailed && (
              <p className="ticket-chat-status" role="alert">
                {text.olderFailed}
              </p>
            )}
            <Button
              size="sm"
              appearance="soft"
              loading={loadingOlder}
              disabled={loadingOlder}
              onClick={loadOlder}
            >
              {loadingOlder ? text.loadingOlder : olderFailed ? text.retry : text.loadOlder}
            </Button>
          </div>
        )}
        {messages.length === 0 && (
          <p className="ticket-chat-status">No messages yet.</p>
        )}
        {messages.map((msg) => (
          <MessageItem
            key={msg._id}
            message={msg}
            isOwn={
              !!currentUserInfo?._id &&
              getRefId(msg.senderId) === currentUserInfo._id
            }
          />
        ))}
      </div>

      <div className="ticket-chat-input-area">
        <Input
          textarea
          className="ticket-chat-textarea"
          value={newMessage}
          placeholder="Type a message..."
          aria-label="Message"
          onChange={(e) => setNewMessage(e.target.value)}
        />

        <div className="ticket-action-row" role="group" aria-label="Status">
          {STATUS_ACTIONS.map((action) => renderAction(action, false))}
        </div>

        <div className="ticket-action-row" role="group" aria-label="Stage">
          {STAGES_ACTIONS.map((action) => renderAction(action, true))}
        </div>
      </div>
    </div>
  );
}
